import { useEffect, useRef } from "react";
import clsx from "clsx";
import type { OrderHistoryRow } from "@/lib/orders";
import type { InvoiceMailOrderHint } from "@/lib/invoiceMail";
import { orderLinePlainText, orderTextWithArticleNumber } from "@/lib/orderDraftUtils";
import { formatRequestAge } from "@/pages/material/maListDisplay";
import {
  deliveryLabel,
  fmtOrderDate,
  splitBestellnummern,
  submissionChannelLabel,
} from "@/pages/bestellungen/orderHistoryDisplay";
import { OrderSourceLinks } from "@/pages/bestellungen/OrderSourceLinks";

export function OrdersHistorySection({
  rows,
  altMode,
  emptyMessage,
  invoiceHints,
  onOpen,
  onViewInvoice,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
}: {
  rows: OrderHistoryRow[];
  altMode: boolean;
  emptyMessage?: string;
  invoiceHints?: Record<number, InvoiceMailOrderHint>;
  onOpen: (row: OrderHistoryRow) => void;
  onViewInvoice?: (row: OrderHistoryRow) => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const armedRef = useRef(true);

  useEffect(() => {
    if (!hasMore || !onLoadMore) return;
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (!entry.isIntersecting) {
          armedRef.current = true;
          return;
        }
        if (!armedRef.current || loadingMore) return;
        armedRef.current = false;
        onLoadMore();
      },
      { rootMargin: "320px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);

  if (!rows.length) {
    return (
      <p className="rounded-ios border border-separator bg-card p-8 text-center text-sm text-label-secondary">
        {emptyMessage || "Keine Bestellungen gefunden."}
      </p>
    );
  }

  return (
    <>
    <ul className="eh-orders-card-list space-y-3" role="list">
      {rows.map((o, index) => (
        <li key={o.id}>
          <OrderCard
            order={o}
            stripe={index % 2 === 0 ? "even" : "odd"}
            altMode={altMode}
            invoiceHint={invoiceHints?.[o.id]}
            onOpen={() => onOpen(o)}
            onViewInvoice={onViewInvoice ? () => onViewInvoice(o) : undefined}
          />
        </li>
      ))}
    </ul>
    {hasMore ? (
      <div ref={sentinelRef} className="flex justify-center py-4 text-sm text-label-tertiary">
        {loadingMore ? "Lade weitere Bestellungen …" : "Weitere Bestellungen laden …"}
      </div>
    ) : (
      <div className="py-4 text-center text-xs text-label-tertiary">Ende der Liste</div>
    )}
    </>
  );
}

export function WerkszeugnisBadge() {
  return <span className="eh-order-badge eh-order-badge--werkszeugnis">Werkszeugnis</span>;
}

function OrderCard({
  order,
  stripe,
  altMode,
  invoiceHint,
  onOpen,
  onViewInvoice,
}: {
  order: OrderHistoryRow;
  stripe: "even" | "odd";
  altMode: boolean;
  invoiceHint?: InvoiceMailOrderHint;
  onOpen: () => void;
  onViewInvoice?: () => void;
}) {
  const cancelled = Boolean(order.cancelled_at);
  const delivered = Boolean(order.geliefert_at);
  const items = Array.isArray(order.positionen) ? order.positionen : [];
  const projects = Array.isArray(order.projects) ? order.projects : [];
  const tkList = splitBestellnummern(order.bestellnummern);
  const contact = (order.supplier_contact_name || "").trim();
  const channelLabel = submissionChannelLabel(order.submission_channel);
  const lieferLabel = deliveryLabel(order);
  const hasSource =
    Boolean(order.source_ma?.id) || (order.anfrage_links?.length ?? 0) > 0;
  const hasInvoice = (invoiceHint?.mail_count ?? 0) > 0;
  const invoicePending = Boolean(invoiceHint?.has_pending);

  return (
    <article
      className={clsx(
        "eh-order-card eh-order-card--clickable overflow-hidden rounded-ios shadow-ios transition",
        stripe === "even" ? "eh-order-card--even" : "eh-order-card--odd",
        cancelled && "eh-order-card--cancelled",
      )}
      aria-labelledby={`order-${order.id}-title`}
    >
      <button
        type="button"
        className="eh-order-card__open block w-full text-left"
        onClick={onOpen}
      >
        <header className="eh-order-card__header">
          <div className="eh-order-card__head-top">
            <div className="eh-order-card__head-main min-w-0">
              <div className="eh-order-card__head-row">
                <h3 id={`order-${order.id}-title`} className="eh-order-card__tk-row">
                  {tkList.length ? (
                    tkList.map((tk) => (
                      <span key={tk} className="eh-order-tk-badge font-mono text-xs font-semibold">
                        {tk}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm font-semibold text-label">—</span>
                  )}
                </h3>
                <span className="eh-order-card__head-sep" aria-hidden="true">·</span>
                <time className="text-xs text-label-secondary" dateTime={order.created_at}>
                  {fmtOrderDate(order.created_at)}
                </time>
                <span className="whitespace-nowrap text-xs font-semibold tabular-nums text-status-orange">
                  {formatRequestAge(order.created_at)}
                </span>
                {delivered ? (
                  <span
                    className="rounded-full border border-status-green/40 bg-status-green/15 px-2 py-0.5 text-xs font-semibold text-status-green"
                  >
                    Geliefert · {fmtOrderDate(order.geliefert_at)}
                  </span>
                ) : null}
                {Number(order.werkszeugnis_count || 0) > 0 ? <WerkszeugnisBadge /> : null}
                {hasInvoice ? (
                  <button
                    type="button"
                    className={
                      invoicePending
                        ? "rounded-full border border-status-orange/40 bg-status-orange/15 px-2 py-0.5 text-xs font-semibold text-status-orange hover:bg-status-orange/25"
                        : "rounded-full border border-accent/30 bg-accent/10 px-2 py-0.5 text-xs font-semibold text-accent hover:bg-accent/20"
                    }
                    title={
                      invoicePending
                        ? "Rechnung im Postfach – noch nicht abgelegt"
                        : "Rechnung eingegangen"
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewInvoice?.();
                    }}
                  >
                    Rechnung{invoiceHint && invoiceHint.mail_count > 1 ? ` (${invoiceHint.mail_count})` : ""}
                  </button>
                ) : null}
                <span className="eh-order-card__positions-badge text-xs text-label-secondary">
                  {items.length} {items.length === 1 ? "Pos." : "Pos."}
                </span>
                {(cancelled || altMode || hasSource || channelLabel) ? (
                  <span className="eh-order-card__inline-badges">
                    {cancelled ? (
                      <span className="eh-order-badge eh-order-badge--cancelled rounded-full border border-status-red/30 bg-status-red/15 px-2 py-0.5 text-xs font-semibold text-status-red">
                        Storniert
                      </span>
                    ) : null}
                    {channelLabel ? (
                      <span className="eh-order-chip eh-order-chip--channel" title="Versandweg">
                        {channelLabel}
                      </span>
                    ) : null}
                    {altMode ? (
                      <span className="eh-order-chip eh-order-chip--warn">Ähnlich</span>
                    ) : null}
                    <OrderSourceLinks order={order} />
                  </span>
                ) : null}
              </div>

              <p className="eh-order-card__supplier-line">
                <span className="font-semibold text-label">{order.supplier_name || "—"}</span>
                {contact ? (
                  <span className="text-label-secondary">
                    {" · "}
                    <span className="text-label">{contact}</span>
                  </span>
                ) : null}
                {lieferLabel ? (
                  <span className="text-label-secondary">
                    {" · "}
                    <span className="font-medium text-label">{lieferLabel}</span>
                  </span>
                ) : null}
              </p>

              {projects.length > 0 ? (
                <ul className="eh-order-card__projects">
                  {projects.map((p) => {
                    const line = [p.auftragsnummer, p.ort, p.beschreibung].filter(Boolean).join(" · ");
                    if (!line) return null;
                    return <li key={p.auftrag_id}>{line}</li>;
                  })}
                </ul>
              ) : null}
            </div>
            <span className="shrink-0 text-xs font-medium text-accent">Details →</span>
          </div>
        </header>

        <section className="eh-order-card__articles" aria-label="Artikelpositionen">
          <div className="eh-order-card__items">
            <p className="eh-order-card__items-label">Artikel</p>
            {items.length === 0 ? (
              <p className="py-2 text-sm text-label-tertiary">Keine Artikel hinterlegt.</p>
            ) : (
              <ul className="eh-order-card__lines">
                {items.slice(0, 4).map((p, idx) => (
                  <li key={`${order.id}-${idx}`} className="eh-order-line text-sm text-label-secondary">
                    <span className="tabular-nums font-medium text-label">{p.menge ?? "—"}</span>
                    <span className="mx-1">{p.einheit || ""}</span>
                    <span className="truncate">
                      {orderLinePlainText(orderTextWithArticleNumber(p.text || "", p.hero_nr)) || "—"}
                    </span>
                    {(p.lot_nummer || "").trim() ? (
                      <span className="ml-2 shrink-0 font-mono text-xs text-label">LOT {p.lot_nummer}</span>
                    ) : null}
                  </li>
                ))}
                {items.length > 4 ? (
                  <li className="text-xs text-label-tertiary">+ {items.length - 4} weitere …</li>
                ) : null}
              </ul>
            )}
          </div>
        </section>
      </button>
    </article>
  );
}
